import { useEffect, useMemo, useRef, useState } from 'react';
import type { Exercise } from '@jungles/shared';
import { api } from '../lib/api';
import { useApi } from '../lib/hooks';
import { useToast } from '../app/toast';
import { Sheet } from './Sheet';
import { Loader } from './Chrome';
import { SearchIcon } from './icons';

/** Каталог упражнений с 3D-иконками и поиском (SPEC §5 T1–T3). */

export function ExercisePicker({
  onPick,
  onClose,
}: {
  onPick: (exercise: Exercise) => void;
  onClose: () => void;
}) {
  const { data, loading, reload } = useApi<Exercise[]>('/api/exercises');
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const alive = useRef(true), pending = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const dismiss = () => { if (!pending.current) onClose(); };

  const groups = useMemo(() => {
    const list = (data ?? []).filter((exercise) =>
      query.trim() ? exercise.name.toLowerCase().includes(query.trim().toLowerCase()) : true,
    );
    const map = new Map<string, Exercise[]>();
    for (const exercise of list) {
      const bucket = map.get(exercise.muscleGroup) ?? [];
      bucket.push(exercise);
      map.set(exercise.muscleGroup, bucket);
    }
    return [...map.entries()];
  }, [data, query]);

  const createCustom = async () => {
    if (pending.current) return;
    const name = query.trim();
    if (name.length < 2) {
      toast('Введите название своего упражнения');
      return;
    }
    pending.current = true;
    setCreating(true);
    try {
      const exercise = await api.post<Exercise>('/api/exercises', { name });
      if (!alive.current) return;
      reload();
      onPick(exercise);
    } catch (error) {
      if (!alive.current) return;
      pending.current = false;
      toast(error instanceof Error ? error.message : 'Не удалось создать упражнение');
      setCreating(false);
    }
  };

  return (
    <Sheet title="Добавить упражнение" onClose={dismiss} full busy={creating}>
      <label className="srch" style={{ marginTop: 10 }}>
        <SearchIcon />
        <span className="vh">Поиск упражнения</span>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Название упражнения"
          autoComplete="off"
        />
        {query ? (
          <button className="clr" type="button" onClick={() => setQuery('')} aria-label="Очистить">
            ×
          </button>
        ) : null}
      </label>

      <div style={{ flex: 1, overflowY: 'auto', marginTop: 12 }}>
        {loading ? <Loader /> : null}

        {groups.map(([group, list]) => (
          <div key={group}>
            <div className="sec">{group}</div>
            <div className="wl">
              {list.map((exercise) => (
                <button
                  key={exercise.id}
                  className="wr"
                  type="button"
                  disabled={creating}
                  onClick={() => { if (!pending.current) onPick(exercise); }}
                >
                  <span className="wi">
                    <img src={exercise.icon} alt="" />
                  </span>
                  <span className="wn">
                    <b>{exercise.name}</b>
                    <small>{exercise.isCustom ? 'Своё упражнение' : exercise.muscleGroup}</small>
                  </span>
                  <span className="chev">+</span>
                </button>
              ))}
            </div>
          </div>
        ))}

        {!loading && groups.length === 0 ? (
          <p className="empty">Ничего не нашли — можно добавить своё упражнение</p>
        ) : null}
      </div>

      <div className="nf">
        <button className="b" type="button" onClick={() => void createCustom()} disabled={creating}>
          + Своё упражнение{query.trim() ? ` «${query.trim()}»` : ''}
        </button>
      </div>
    </Sheet>
  );
}
